import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

/** CodeMirror theme driven entirely by the design tokens. No glow on text. */
export const cortexTheme = EditorView.theme(
  {
    '&': { backgroundColor: 'var(--bg-base)', color: 'var(--human)', height: '100%' },
    '.cm-scroller': { fontFamily: 'var(--font-ui)', fontSize: '15.5px', lineHeight: '1.65' },
    '.cm-content': { padding: '24px 0 60vh', caretColor: 'var(--teal)', maxWidth: '78ch', margin: '0 auto', paddingLeft: '32px', paddingRight: '32px' },
    '.cm-line': { padding: '0' },
    '&.cm-focused': { outline: 'none', boxShadow: 'none' },
    '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--teal)', borderLeftWidth: '2px' },
    '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground': { background: 'rgba(94, 200, 192, 0.18)' },
    '.cm-activeLine': { backgroundColor: 'rgba(255,255,255,0.025)' },
    '.cm-gutters': { display: 'none' },
    '.cm-matchingBracket': { backgroundColor: 'rgba(94,200,192,0.15)', outline: '1px solid var(--teal-dim)' },
    '.cm-selectionMatch': { backgroundColor: 'rgba(245,124,31,0.15)' },
    '.cm-searchMatch': { backgroundColor: 'rgba(251,191,36,0.2)', outline: '1px solid rgba(251,191,36,0.4)' },
    '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: 'rgba(245,124,31,0.35)' },
    '.cm-wikilink': { color: 'var(--teal)', borderBottom: '1px dotted var(--teal-dim)', cursor: 'pointer' },
    '.cm-wikilink:hover': { borderBottomStyle: 'solid' },
    '.cm-frontmatter': { fontFamily: 'var(--font-mono)', fontSize: '12.5px', color: 'var(--text-faint)', background: 'rgba(255,255,255,0.02)' },
    '.cm-frontmatter-fence': { color: 'var(--line-strong)' },
    '.cm-tooltip': { background: 'var(--bg-raised)', border: '1px solid var(--line-subtle)', color: 'var(--human)', boxShadow: '0 8px 30px rgba(0,0,0,0.5)' },
    '.cm-tooltip.cm-tooltip-autocomplete > ul': { fontFamily: 'var(--font-ui)', fontSize: '13px', maxHeight: '320px' },
    '.cm-tooltip.cm-tooltip-autocomplete > ul > li': { padding: '3px 8px' },
    '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': { background: 'rgba(94,200,192,0.12)', color: 'var(--teal)' },
    '.cm-completionDetail': { fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--text-faint)', marginLeft: '8px', fontStyle: 'normal' },
    '.cm-completionIcon': { display: 'none' },
    '.cm-panels': { background: 'var(--bg-panel)', color: 'var(--human)', borderColor: 'var(--line-faint)' },
    '.cm-panels input': { background: 'var(--bg-input)', border: '1px solid var(--line-subtle)', color: 'var(--human)' },
    '.cm-panels button': { background: 'var(--bg-raised)', color: 'var(--text-dim)', border: '1px solid var(--line-subtle)', backgroundImage: 'none' },
    '.cm-placeholder': { color: 'var(--text-faint)' },
  },
  { dark: true },
);

const md = HighlightStyle.define([
  { tag: t.heading1, fontSize: '1.6em', fontWeight: '600', lineHeight: '1.25' },
  { tag: t.heading2, fontSize: '1.3em', fontWeight: '600', lineHeight: '1.3' },
  { tag: t.heading3, fontSize: '1.12em', fontWeight: '600' },
  { tag: [t.heading4, t.heading5, t.heading6], fontWeight: '600' },
  { tag: t.strong, fontWeight: '700' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through', color: 'var(--text-faint)' },
  { tag: t.monospace, fontFamily: 'var(--font-mono)', fontSize: '0.9em', color: 'var(--orange)' },
  { tag: t.link, color: 'var(--teal)' },
  { tag: t.url, color: 'var(--teal-dim)', fontFamily: 'var(--font-mono)', fontSize: '0.9em' },
  { tag: t.quote, color: 'var(--text-dim)' },
  { tag: t.list, color: 'var(--text-dim)' },
  { tag: t.contentSeparator, color: 'var(--line-strong)' },
  { tag: t.processingInstruction, color: 'var(--text-faint)' },
  { tag: t.meta, color: 'var(--text-faint)' },
  { tag: t.labelName, color: 'var(--orange)' },
  { tag: t.comment, color: 'var(--text-faint)' },
  { tag: t.keyword, color: 'var(--info)' },
  { tag: t.string, color: 'var(--ok)' },
  { tag: t.number, color: 'var(--orange)', fontVariantNumeric: 'tabular-nums' },
  { tag: t.propertyName, color: 'var(--teal)' },
  { tag: t.atom, color: 'var(--warn)' },
]);

export const cortexHighlight = syntaxHighlighting(md);
